"use client";

import {
  IMPROMPTU_PROMPT_CATEGORIES,
  type ImpromptuPrompt,
  type ImpromptuPromptCategory,
  toImpromptuPromptSelection,
} from "@/lib/impromptu/config";
import { IMPROMPTU_COPY } from "@/lib/impromptu/copy";
import { ArrowUpRight, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const COPY = IMPROMPTU_COPY.topicLibrary;

/**
 * How many rows are added per press. Named because it is used three times — the
 * initial limit, the increment, and the label on the button — and a mismatch
 * between any two of them is a button that lies about what it will do.
 */
const PAGE_SIZE = 16;

/** Shared so the focus treatment matches the rest of the marketing surface. */
const FOCUS =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A8431F] focus-visible:ring-offset-2 focus-visible:ring-offset-white";

type TopicLibraryProps = {
  prompts: ReadonlyArray<ImpromptuPrompt>;
  onSelect: (prompt: ReturnType<typeof toImpromptuPromptSelection>) => void;
};

type CategoryFilter = "all" | ImpromptuPromptCategory;

export function TopicLibrary({ prompts, onSelect }: TopicLibraryProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<CategoryFilter>("all");
  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const visiblePrompts = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return prompts.filter((prompt) => {
      const matchesCategory = category === "all" || prompt.category === category;
      const matchesQuery =
        !normalizedQuery || prompt.text.toLocaleLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesQuery;
    });
  }, [category, prompts, query]);
  const displayedPrompts = visiblePrompts.slice(0, visibleLimit);
  const remaining = visiblePrompts.length - displayedPrompts.length;

  useEffect(() => setVisibleLimit(PAGE_SIZE), [category, query]);

  const selectPrompt = (prompt: ImpromptuPrompt) => {
    onSelect(toImpromptuPromptSelection(prompt));
    document.getElementById("impromptu-studio")?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "center",
    });
  };

  return (
    <section
      id="topic-library"
      className="scroll-mt-24 bg-white py-16 md:py-24"
      aria-labelledby="topic-library-title"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-end lg:gap-12">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#A8431F]">
              {COPY.eyebrow}
            </p>
            <h2
              id="topic-library-title"
              className="mt-4 max-w-[16ch] text-[2rem] font-extrabold leading-[1.18] text-[#1C1C1E]"
            >
              {COPY.heading}
            </h2>
          </div>
          <div>
            <p className="max-w-[38rem] text-lg leading-[1.6] text-[#2C2C2E]">
              {COPY.subheading(prompts.length)}
            </p>
            <label className="relative mt-6 block" htmlFor="impromptu-topic-search">
              <span className="sr-only">{COPY.searchLabel}</span>
              <Search
                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8E8E93]"
                aria-hidden="true"
              />
              <input
                id="impromptu-topic-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={COPY.searchPlaceholder}
                className="min-h-[3.25rem] w-full rounded-xl border-2 border-[#E5E5EA] bg-white pl-12 pr-4 text-base text-[#1C1C1E] outline-none transition-colors placeholder:text-[#8E8E93] hover:border-[#FD954F] focus:border-[#C05C2F]"
              />
            </label>
          </div>
        </div>

        <div
          className="mt-10 flex gap-2 overflow-x-auto pb-2"
          role="group"
          aria-label={COPY.filterGroupLabel}
        >
          {(
            [
              ["all", COPY.filterAll(prompts.length)],
              ...IMPROMPTU_PROMPT_CATEGORIES.map(
                (item) =>
                  [
                    item.value,
                    `${item.label} ${prompts.filter((prompt) => prompt.category === item.value).length}`,
                  ] as const
              ),
            ] as ReadonlyArray<readonly [CategoryFilter, string]>
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setCategory(value)}
              aria-pressed={category === value}
              className={`min-h-11 shrink-0 rounded-full border px-4 text-sm font-bold transition-colors ${FOCUS} ${
                category === value
                  ? "border-[#1C1C1E] bg-[#1C1C1E] text-white"
                  : "border-[#E5E5EA] text-[#2C2C2E] hover:border-[#1C1C1E]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-8 border-t border-[#E5E5EA]">
          {displayedPrompts.map((prompt, index) => (
            <button
              key={prompt.id}
              type="button"
              onClick={() => selectPrompt(prompt)}
              className={`group grid w-full gap-2 border-b border-[#E5E5EA] py-5 text-left transition-colors hover:bg-[#F5F5F0] sm:grid-cols-[3.5rem_9rem_1fr_7rem] sm:items-center sm:gap-4 sm:px-3 ${FOCUS} focus-visible:ring-inset`}
            >
              <span className="hidden text-xs font-semibold tabular-nums text-[#8E8E93] sm:block">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#A8431F]">
                {IMPROMPTU_PROMPT_CATEGORIES.find((item) => item.value === prompt.category)?.label}
              </span>
              <span className="max-w-3xl text-base font-bold leading-[1.5] text-[#1C1C1E] sm:text-lg">
                {prompt.text}
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#8E8E93] transition-colors group-hover:text-[#A8431F] sm:justify-end">
                {COPY.practise}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
          ))}
        </div>

        {/* The label counts what is actually left, so the last press cannot promise
            a full page and deliver three. */}
        {remaining > 0 && (
          <button
            type="button"
            onClick={() => setVisibleLimit((limit) => limit + PAGE_SIZE)}
            className={`mt-8 inline-flex min-h-12 items-center rounded-xl border-2 border-[#E5E5EA] px-6 font-bold text-[#1C1C1E] transition-colors hover:border-[#1C1C1E] hover:bg-[#F5F5F0] ${FOCUS}`}
          >
            {COPY.showMore(Math.min(remaining, PAGE_SIZE))}
          </button>
        )}

        {visiblePrompts.length === 0 && (
          <p className="border-b border-[#E5E5EA] py-12 text-center text-[#2C2C2E]">{COPY.empty}</p>
        )}
      </div>
    </section>
  );
}
